// src/components/question/QuestionFilters.tsx
// ✅ REFATORADO: Filtros hierárquicos com 4 selects dinâmicos + filtros avançados

import React, { useState, useEffect, useCallback } from 'react';
import { X, SlidersHorizontal, ChevronDown, Lock } from 'lucide-react';
import { QuestionFilters as Filters } from '../../types/question.types';
import { questionService } from '../../services/questionService';
import { examService } from '../../services/examService';
import { useAuth } from '../../contexts/AuthContext';
import './QuestionFilters.css';

interface CategoryNode {
    _id: string;
    name: string;
    level: number;
    questionsCount: number;
    children: CategoryNode[];
}

interface QuestionFiltersProps {
    onFiltersChange: (filters: Filters) => void;
    initialFilters?: Filters;
    totalQuestions?: number;
    loading?: boolean;
}

// ── Flatten de árvore preservando parentId ──
function flattenTree(nodes: CategoryNode[], parentId: string | null = null): Array<{
    _id: string; name: string; level: number; parentId: string | null; questionsCount: number;
}> {
    const result: any[] = [];
    for (const node of nodes) {
        result.push({ _id: node._id, name: node.name, level: node.level, parentId, questionsCount: node.questionsCount });
        if (node.children?.length) {
            result.push(...flattenTree(node.children, node._id));
        }
    }
    return result;
}

const DIFFICULTY_OPTIONS = [
    { value: 'facil', label: 'Fácil', color: '#5FBA61' },
    { value: 'medio', label: 'Médio', color: '#D4B35D' },
    { value: 'dificil', label: 'Difícil', color: '#EF4444' },
];

const STATUS_OPTIONS = [
    { value: 'false', label: 'Não respondidas' },
    { value: 'true', label: 'Respondidas' },
    { value: 'correct', label: 'Acertadas' },
    { value: 'wrong', label: 'Erradas' },
];

const PUBLISH_STATUS_OPTIONS_ADMIN = [
    { value: '', label: 'Todos' },
    { value: 'publicado', label: 'Publicados' },
    { value: 'rascunho', label: 'Rascunho' },
    { value: 'analise', label: 'Em Análise' },
    { value: 'prova', label: 'Exclusiva Prova' },
];

const PUBLISH_STATUS_OPTIONS_REVISOR = [
    { value: '', label: 'Todos' },
    { value: 'publicado', label: 'Publicados' },
    { value: 'analise', label: 'Em Análise' },
];

const QuestionFilters: React.FC<QuestionFiltersProps> = ({
    onFiltersChange,
    initialFilters = {},
    totalQuestions,
    loading = false,
}) => {
    const { isAdmin, isRevisor } = useAuth();
    const [allCategories, setAllCategories] = useState<ReturnType<typeof flattenTree>>([]);
    const [exams, setExams] = useState<Array<{ _id: string; name: string; year: number | null; institution: string | null }>>([]);
    const [filters, setFilters] = useState<Filters>(initialFilters);

    // DEPOIS — oculta categorias sem questões publicadas
    const level1Options = allCategories.filter(c => c.level === 1 && c.questionsCount > 0);
    const level2Options = allCategories.filter(c => c.level === 2 && c.questionsCount > 0 && (!filters.level1Id || c.parentId === filters.level1Id));
    const level3Options = allCategories.filter(c => c.level === 3 && c.questionsCount > 0 && (!filters.level2Id || c.parentId === filters.level2Id));
    const level4Options = allCategories.filter(c => c.level === 4 && c.questionsCount > 0 && (!filters.level3Id || c.parentId === filters.level3Id));

    // Derived: years from exams + filtered exam list
    const uniqueYears = Array.from(new Set(exams.map(e => e.year).filter((y): y is number => y !== null))).sort((a, b) => b - a);
    const filteredExams = filters.year
        ? exams.filter(e => e.year === filters.year)
        : exams;

    useEffect(() => {
        questionService.getCategoriesTree()
            .then(tree => setAllCategories(flattenTree(tree)))
            .catch(err => console.error('Erro ao carregar categorias:', err));

        const fetchExams = isAdmin ? examService.getExamsAdmin() : examService.getExams();
        fetchExams
            .then(list => setExams(list.map(e => ({ _id: e._id as string, name: e.name, year: e.year, institution: e.institution }))))
            .catch(() => { /* filtro fica vazio */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilter = useCallback((newFilters: Filters) => {
        setFilters(newFilters);
        onFiltersChange(newFilters);
    }, [onFiltersChange]);

    const handleLevelChange = (level: 1 | 2 | 3 | 4, id: string) => {
        const newFilters = { ...filters };

        if (level === 1) {
            newFilters.level1Id = id || undefined;
            delete newFilters.level2Id;
            delete newFilters.level3Id;
            delete newFilters.level4Id;
            // Também limpa os nomes legados se existirem
            delete newFilters.level1;
            delete newFilters.level2;
            delete newFilters.level3;
            delete newFilters.level4;
        } else if (level === 2) {
            newFilters.level2Id = id || undefined;
            delete newFilters.level3Id;
            delete newFilters.level4Id;
            delete newFilters.level2;
            delete newFilters.level3;
            delete newFilters.level4;
        } else if (level === 3) {
            newFilters.level3Id = id || undefined;
            delete newFilters.level4Id;
            delete newFilters.level3;
            delete newFilters.level4;
        } else {
            newFilters.level4Id = id || undefined;
            delete newFilters.level4;
        }

        applyFilter(newFilters);
    };

    const handleDifficulty = (value: string) => {
        const newFilters = { ...filters, difficulty: filters.difficulty === value ? undefined : value as Filters['difficulty'] };
        applyFilter(newFilters);
    };

    const handleStatus = (value: string) => {
        const newFilters = { ...filters, answered: filters.answered === value ? undefined : value as Filters['answered'] };
        applyFilter(newFilters);
    };

    const handlePublishStatus = (value: string) => {
        const newFilters = { ...filters, publishStatus: value ? value as Filters['publishStatus'] : undefined };
        applyFilter(newFilters);
    };

    const handleExamChange = (examId: string) => {
        const exam = exams.find(e => e._id === examId);
        const newFilters: Filters = { ...filters, examId: examId || undefined };
        if (exam) newFilters.year = exam.year ?? undefined;
        else if (!examId) delete newFilters.examId;
        applyFilter(newFilters);
    };

    const handleYearChange = (year: string) => {
        const newFilters: Filters = { ...filters, year: year ? Number(year) : undefined };
        // Clear exam selection if it doesn't belong to the new year
        if (year && filters.examId) {
            const currentExam = exams.find(e => e._id === filters.examId);
            if (currentExam && currentExam.year !== Number(year)) delete newFilters.examId;
        }
        if (!year) delete newFilters.examId;
        applyFilter(newFilters);
    };

    const clearFilters = () => applyFilter({});

    const activeCount = Object.values(filters).filter(v => v !== undefined && v !== '' && v !== 'all').length;

    // Nomes selecionados para label dos selects
    const getName = (id?: string) => id ? (allCategories.find(c => c._id === id)?.name ?? '') : '';

    const activeBreadcrumb = [
        { id: filters.level1Id },
        { id: filters.level2Id },
        { id: filters.level3Id },
        { id: filters.level4Id },
    ].filter(b => b.id);

    return (
        <div className="qf-panel">

            {/* ════ HEADER SÓLIDO ════ */}
            <div className="qf-header">
                <div className="qf-header-left">
                    <SlidersHorizontal size={15} className="qf-header-icon" />
                    <span className="qf-header-title">Filtros</span>
                    {activeCount > 0 && (
                        <span className="qf-active-badge">{activeCount}</span>
                    )}
                </div>
                <div className="qf-header-right">
                    {totalQuestions !== undefined && (
                        <span className="qf-total-preview">
                            <strong>{loading ? '…' : totalQuestions}</strong> questões
                        </span>
                    )}
                    {activeCount > 0 && (
                        <button className="qf-clear-btn" onClick={clearFilters}>
                            <X size={13} /> Limpar tudo
                        </button>
                    )}
                </div>
            </div>

            {/* ════ CORPO ════ */}
            <div className="qf-body">

                {/* ── Seção 1: Hierarquia ── */}
                <div className="qf-section">
                    <div className="qf-section-header">
                        <span className="qf-section-label">Hierarquia</span>
                        {activeBreadcrumb.length > 0 && (
                            <div className="qf-breadcrumb">
                                {activeBreadcrumb.map((b, i, arr) => (
                                    <React.Fragment key={b.id}>
                                        <span className="qf-bc-item">{getName(b.id)}</span>
                                        {i < arr.length - 1 && <span className="qf-bc-sep">›</span>}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="qf-row qf-row--hierarchy">

                        <div className="qf-select-group">
                            <label className="qf-select-label">Campo de Estudo</label>
                            <div className="qf-select-wrapper">
                                <select
                                    className={`qf-select ${filters.level1Id ? 'qf-select--active' : ''}`}
                                    value={filters.level1Id ?? ''}
                                    onChange={e => handleLevelChange(1, e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {level1Options.map(c => (
                                        <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>
                                    ))}
                                </select>
                                <ChevronDown size={14} className="qf-select-icon" />
                            </div>
                        </div>

                        <div className="qf-select-group">
                            <label className="qf-select-label">Assunto</label>
                            {!filters.level1Id ? (
                                <div className="qf-select-hint"><Lock size={12} /><span>Selecione um campo</span></div>
                            ) : (
                                <div className="qf-select-wrapper">
                                    <select
                                        className={`qf-select ${filters.level2Id ? 'qf-select--active' : ''} ${level2Options.length === 0 ? 'qf-select--empty' : ''}`}
                                        value={filters.level2Id ?? ''}
                                        onChange={e => handleLevelChange(2, e.target.value)}
                                        disabled={level2Options.length === 0}
                                    >
                                        <option value="">Todos</option>
                                        {level2Options.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                    </select>
                                    <ChevronDown size={14} className="qf-select-icon" />
                                </div>
                            )}
                        </div>

                        <div className="qf-select-group">
                            <label className="qf-select-label">Tema</label>
                            {!filters.level2Id ? (
                                <div className="qf-select-hint"><Lock size={12} /><span>Selecione um assunto</span></div>
                            ) : (
                                <div className="qf-select-wrapper">
                                    <select
                                        className={`qf-select ${filters.level3Id ? 'qf-select--active' : ''} ${level3Options.length === 0 ? 'qf-select--empty' : ''}`}
                                        value={filters.level3Id ?? ''}
                                        onChange={e => handleLevelChange(3, e.target.value)}
                                        disabled={level3Options.length === 0}
                                    >
                                        <option value="">Todos</option>
                                        {level3Options.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                    </select>
                                    <ChevronDown size={14} className="qf-select-icon" />
                                </div>
                            )}
                        </div>

                        <div className="qf-select-group">
                            <label className="qf-select-label">Subtema</label>
                            {!filters.level3Id ? (
                                <div className="qf-select-hint"><Lock size={12} /><span>Selecione um tema</span></div>
                            ) : (
                                <div className="qf-select-wrapper">
                                    <select
                                        className={`qf-select ${filters.level4Id ? 'qf-select--active' : ''} ${level4Options.length === 0 ? 'qf-select--empty' : ''}`}
                                        value={filters.level4Id ?? ''}
                                        onChange={e => handleLevelChange(4, e.target.value)}
                                        disabled={level4Options.length === 0}
                                    >
                                        <option value="">Todos</option>
                                        {level4Options.map(c => <option key={c._id} value={c._id}>{c.name} ({c.questionsCount})</option>)}
                                    </select>
                                    <ChevronDown size={14} className="qf-select-icon" />
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                <div className="qf-divider" />

                {/* ── Seção 2: Filtros Rápidos ── */}
                <div className="qf-section">
                    <span className="qf-section-label">Filtros Rápidos</span>
                    <div className="qf-row qf-row--chips">

                        {(isAdmin || isRevisor) && (
                            <div className="qf-chip-group">
                                <span className="qf-chip-label">Publicação</span>
                                <div className="qf-select-wrapper qf-select-wrapper--sm">
                                    <select
                                        className={`qf-select ${filters.publishStatus ? 'qf-select--active' : ''}`}
                                        value={filters.publishStatus ?? ''}
                                        onChange={e => handlePublishStatus(e.target.value)}
                                    >
                                        {(isAdmin ? PUBLISH_STATUS_OPTIONS_ADMIN : PUBLISH_STATUS_OPTIONS_REVISOR).map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={14} className="qf-select-icon" />
                                </div>
                            </div>
                        )}

                        <div className="qf-chip-group">
                            <span className="qf-chip-label">Dificuldade</span>
                            <div className="qf-chips">
                                {DIFFICULTY_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`qf-chip ${filters.difficulty === opt.value ? 'qf-chip--active' : ''}`}
                                        style={filters.difficulty === opt.value ? { '--chip-color': opt.color } as React.CSSProperties : undefined}
                                        onClick={() => handleDifficulty(opt.value)}
                                    >
                                        <span className="qf-chip-dot" style={{ background: opt.color }} />
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="qf-chip-group">
                            <span className="qf-chip-label">Status</span>
                            <div className="qf-chips">
                                {STATUS_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`qf-chip ${filters.answered === opt.value ? 'qf-chip--active' : ''}`}
                                        onClick={() => handleStatus(opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {exams.length > 0 && (
                            <div className="qf-chip-group">
                                <span className="qf-chip-label">Ano</span>
                                <div className="qf-select-wrapper qf-select-wrapper--sm">
                                    <select
                                        className={`qf-select ${filters.year ? 'qf-select--active' : ''}`}
                                        value={filters.year ?? ''}
                                        onChange={e => handleYearChange(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <ChevronDown size={14} className="qf-select-icon" />
                                </div>
                            </div>
                        )}

                        {exams.length > 0 && (
                            <div className="qf-chip-group">
                                <span className="qf-chip-label">Prova</span>
                                <div className="qf-select-wrapper qf-select-wrapper--sm">
                                    <select
                                        className={`qf-select ${filters.examId ? 'qf-select--active' : ''}`}
                                        value={filters.examId ?? ''}
                                        onChange={e => handleExamChange(e.target.value)}
                                    >
                                        <option value="">Todas</option>
                                        {filteredExams.map(e => (
                                            <option key={e._id} value={e._id}>{e.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={14} className="qf-select-icon" />
                                </div>
                            </div>
                        )}

                    </div>
                </div>

            </div>
        </div>
    );
};

export default QuestionFilters;
